import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import Link from '@mui/material/Link';
import translator from '@Components/Utilities/Translator';
import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { sandhillsRequest, addAuctionSiteDLVs, sandhillsLogging, getBidAgreementRefTypeFromECID } from '@Components/Utilities/SandhillsUtility';
import TranslationText from '@Components/Utilities/TranslationText';
import { bidderStatus, bidCallerCollectionTypes, INT32_MAX_VALUE, gtmEventType } from '@Components/Utilities/SandhillsConstants';
import BiddingAgreementModal from '@Components/Shared/Modals/BiddingAgreementModal'
import EnableQuickBidModal from '@Components/Shared/Modals/EnableQuickBidModal'
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { extendAuctionTimeBidding, placeAuctionTimeBid } from '@Components/Utilities/AuctionTimeBidding';

import { addBidAgreementTextToSessionStorage, addUpdateBAAcceptanceToSessionStorage, doesBrowserSessionShowUserAlreadyAcceptedBidAgreement, getBidAgreementTextForSaleFromSessionStorage, isSessionStorageSupported } from '@Components/Utilities/SaleBidAgreement';

const ListingBiddingBox = (props) => {
    const routes = useRecoilValue(atoms.routes);
    const currentUser = useRecoilValue(atoms.currentUser);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const [bidValue, setBidValue] = useState(null);     
    const [biddingAgreementData, setBiddingAgreementData] = useState(props.BiddingAgreementData);
    const [showBiddingAgreement, setShowBiddingAgreement] = useState(false);    
    const [biddingStatusMessage, setBiddingStatusMessage] = useState(null);
    const [showQuickBidMoreInfo, setShowQuickBidMoreInfo] = useState(false);
    const [showQuickBidActivationModal, setShowQuickBidActivationModal] = useState(false);
    const [showOnlyQuickBidButton, setShowOnlyQuickBidButton] = useState(props.showOnlyQuickBidButton);
    const [validationError, setValidationError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);    

    const [bidErrorDetails, setBidErrorDetails] = useState({
        errorMessage: '',
        highlightedBidIncrement: 0,
        autoBidIncrementData: null
    });

    const [quickBiddingEnabled, setQuickBiddingEnabled] = useState(props.isQuickBiddingEnabled);    

    const isEFBidderWithPendingVettingBidderStatus = currentUser?.IsAuthenticated && props.IsEF && biddingAgreementData?.BidAgreement?.VettingBidderStatus == bidderStatus.PENDING;
    const disablePlaceBidButton = props.IsDetailPage && (currentUser?.BidderStatus != bidderStatus.ACTIVE || (biddingAgreementData && !biddingAgreementData?.BidAgreement?.UserIsApproved));

    const bidAgreementRequiredToBid = props.AuctionBiddingData?.BidAgreementAcceptanceRequiredForBidding;    

    const onBidValueChange = (e) => {
        if (e?.target?.value && !isNaN(e.target.value)) {
            setValidationError('');
            setBidValue(e.target.value);
        }
        else {
            setValidationError(translator.translate('sandhills.tradepubwebsite.mvc.invalidbidnumberonly'));
            if (e?.target?.value == "") {
                setBidValue(""); // Allow them to clear out the box completely
            }
        }
    };
    
    const onBiddingAgreementDataUpdated = (biddingAgreementData) => {
        if (biddingAgreementData) {
            setBiddingAgreementData(biddingAgreementData);
            if (biddingAgreementData.BidAgreement.VettingBidderStatus && biddingAgreementData.BidAgreement.VettingBidderStatus == bidderStatus.PENDING && !biddingAgreementData.BidAgreement.UserIsApproved && !biddingAgreementData.BidAgreement.PreAuthRequired) {
                setBiddingStatusMessage(translator.translate('sandhills.tradepubwebsite.mvc.dealerreviewingrightsphone', [props.dealerName, biddingAgreementData.EventPhoneNumber]));
                setBidErrorDetails({
                    errorMessage: translator.translate('sandhills.tradepubwebsite.mvc.dealerreviewingrightsphone', [props.dealerName, biddingAgreementData.EventPhoneNumber])
                });
                addUpdateBAAcceptanceToSessionStorage(currentUser?.UserAuthID, props.AuctionBiddingData?.SaleID, false);
                setShowBiddingAgreement(true);                
            }
            else if (biddingAgreementData?.BidAgreement?.VettingBidderStatus == bidderStatus.REVOKED) {
                setBiddingStatusMessage(translator.translate('sandhills.bidcallerboard.mvc.dealerdeniedrights', [props.dealerName]));
                setBidErrorDetails({
                    errorMessage: translator.translate('sandhills.tradepubwebsite.mvc.dealerreviewingrightsphone', [props.dealerName, biddingAgreementData.EventPhoneNumber])
                });
                addUpdateBAAcceptanceToSessionStorage(currentUser?.UserAuthID, props.AuctionBiddingData?.SaleID, false);
                setShowBiddingAgreement(true);                
            }
            else if (!biddingAgreementData.BidAgreement.UserIsApproved) {
                addUpdateBAAcceptanceToSessionStorage(currentUser?.UserAuthID, props.AuctionBiddingData?.SaleID, false);    //Reset flag and this should force bid agreement check later to fetch bid agreement status from server.                
            }
            else if (biddingAgreementData.BidAgreement.UserIsApproved) {
                addUpdateBAAcceptanceToSessionStorage(currentUser?.UserAuthID, props.AuctionBiddingData?.SaleID, true);
               
                if (showOnlyQuickBidButton == true) {
                    setShowBiddingAgreement(false);
                    attemptQuickBid();
                }
            }
        } 
    };

    const validateBid = () => {
        if (bidValue > INT32_MAX_VALUE) {
            let bidTranslated = translator.translate('sandhills.tradepubwebsite.mvc.bid');
            setValidationError(translator.translate('sandhills.FieldMustBeLessThan', [bidTranslated, INT32_MAX_VALUE.toString()]));
            return false;
        }
        else if (bidValue <= 0) {
            setValidationError(translator.translate('sandhills.tradepubwebsite.mvc.bidmustbegreaterthan0'));
            return false;
        }
        else if (props.AuctionBiddingData?.UserBidInfo?.UsersLastBid && bidValue == props.AuctionBiddingData?.UserBidInfo.UsersLastBid) {
            setValidationError(translator.translate('sandhills.tradepubwebsite.mvc.bidnotlargerthanusermax'));
            return false;
        }
        else if (props.AuctionBiddingData?.UserBidInfo?.UsersLastBid && bidValue < props.AuctionBiddingData?.UserBidInfo.UsersLastBid) {
            setValidationError(translator.translate('sandhills.tradepubwebsite.mvc.bidnotlargerthanusermax'));
            return false;
        } else if (props.IsAT) {

            if (isNaN(props.AuctionBiddingData.Increment)) {
                setValidationError(translator.translate('sandhills.tradepubwebsite.mvc.therewereproblemspleaserefresh'));
                return false;
            }
            else {
                // Javascript does not handle small decimal numbers well and will not produce accurate modukus output once we reach under 0.25 cents
                // We'll scale the numbers in an attempt to get around the JS floating-point precision issues
                let scaleFactor = 10000; // 10000 to handle up to four decimal places
                let scaledValue = bidValue * scaleFactor;

                if (props.AutoBidIncrementData) {
                    if (!validateBidForAutoBidIncrement(scaledValue, scaleFactor)) {
                        return false;
                    }
                } else {
                    let scaledIncrement = props.AuctionBiddingData.Increment * scaleFactor;

                    if (scaledValue % scaledIncrement !== 0) {
                        setValidationError(translator.translate('sandhills.tradepubwebsite.mvc.bidmustbeincrement').replace("{0}", props.AuctionBiddingData.Increment));
                        return false;
                    }
                }

                //For AT toFixed(2) sets string type for displaying, used to keep it 2 decimal places on input and for displaying ".00". 
                //All operating types except addition(+) will coerce to number but when using + it will String concat instead not add.
                if (typeof bidValue === 'string') {
                    setBidValue(parseFloat(bidValue));
                }
            }
        }
        setValidationError('');
        return true;
    };

    const validateBidForAutoBidIncrement = (scaledValue, scaleFactor) => {
        // Find the appropriate range for the current bid
        const matchingRange = props.AutoBidIncrementData.AutoBidIncrementRanges.find(range => {
            return bidValue >= range.MinimumBidValue &&
                (range.MaxBidValue === null || bidValue <= range.MaxBidValue);
        });

        if (matchingRange) {            
            // Check if bid is valid increment        
            let scaledIncrement = matchingRange.BidIncrementValue * scaleFactor;

            if (scaledValue % scaledIncrement !== 0) {
                setBidErrorDetails({
                    errorMessage: translator.translate('sandhills.invalidbidforautobidincrementerrorshowranges'),
                    autoBidIncrementData: props.AutoBidIncrementData,
                    highlightedBidIncrement: matchingRange.BidIncrementValue
                });
                setShowBiddingAgreement(true); // Reuse the bidding agreement modal to show the auto bid increment error and ranges
                return false;
            } else {
                setBidErrorDetails({
                    errorMessage: '',
                    autoBidIncrementData: 0,
                    highlightedBidIncrement: null
                });
            }
        }

        return true;
    }
    
    const submitQuickBid = () => {        
        if (props.IsAT && biddingAgreementData?.BidAgreement?.UserIsApproved && props.AuctionBiddingData && !isLoading) {
            attemptQuickBid();
        }
        else if (props.IsAT && !(biddingAgreementData?.BidAgreement?.UserIsApproved && props.AuctionBiddingData && !isLoading)) {
            if (bidAgreementRequiredToBid && (!biddingAgreementData || (isSessionStorageSupported() && doesBrowserSessionShowUserAlreadyAcceptedBidAgreement(currentUser?.UserAuthID, props.AuctionBiddingData?.SaleID)))) {
                
                let getBiddingAgreementURL = routes.GetBiddingAgreementWithRefType.replace("{0}", getBidAgreementRefTypeFromECID(props.AuctionBiddingData?.EventCategoryID)).replace("{1}", props.AuctionBiddingData.SaleID);

                setIsLoading(true);

                sandhillsRequest({
                    url: getBiddingAgreementURL,
                    errorHandling: {
                        message: 'An error occurred while retrieving the Terms and Conditions. Please refresh your page and try again.',
                        useConsole: false,
                        useAlert: true
                    }
                }).then((data) => {
                    if (data?.BidAgreement) {
                        setBiddingAgreementData(data);
                        setIsLoading(false);
                        if (data.BidAgreement.VettingBidderStatus && data.BidAgreement.VettingBidderStatus == bidderStatus.PENDING && !data.BidAgreement.UserIsApproved && !data.BidAgreement.PreAuthRequired) {                            
                            setBidErrorDetails({
                                errorMessage: translator.translate('sandhills.tradepubwebsite.mvc.dealerreviewingrightsphone', [props.dealerName, data.EventPhoneNumber])                                
                            });
                            setShowBiddingAgreement(true);
                        }
                        else if (data?.BidAgreement?.VettingBidderStatus == bidderStatus.REVOKED) {                            
                            setBidErrorDetails({
                                errorMessage: translator.translate('sandhills.bidcallerboard.mvc.dealerdeniedrights', [props.dealerName])
                            });
                            setShowBiddingAgreement(true);
                        }
                        else {
                            if (data?.BidAgreement?.UserIsApproved && showOnlyQuickBidButton == true) {
                                attemptQuickBid();
                            } else {
                                setShowBiddingAgreement(true);
                            }
                        }
                    }
                    else {
                        alert('An error occurred while retrieving the Terms and Conditions. Please refresh your page and try again.');
                    }
                })
                    .catch((error) => {
                        setIsLoading(false);
                        setShowBiddingAgreement(false);
                    });
            } else if (!bidAgreementRequiredToBid) {
                attemptQuickBid();
            }
            else {
                if (biddingAgreementData?.BidAgreement.VettingBidderStatus == bidderStatus.REVOKED) {
                    setBidErrorDetails({
                        errorMessage: translator.translate('sandhills.bidcallerboard.mvc.dealerdeniedrights', [props.dealerName])
                    });
                }
                setShowBiddingAgreement(true);
                setIsLoading(false);
            }
        }
    };

    const attemptQuickBid = () => {
        const nextBid = ((props.AuctionBiddingData?.CurrentBid || 0) + props.AuctionBiddingData.Increment).toFixed(2);
        props.AuctionBiddingData.UserBid = nextBid;

        setIsLoading(true);
        props.setWaitingForQuickBidAttemptResponseFlag(true);
        placeAuctionTimeBid(routes, props.AuctionBiddingData, true).then((data) => {
            setIsLoading(false);
            if (data.Success) {
                const skipListingDataLookup = props.IsAT;   //Skip listing data lookup for AT listings because if on list page, this can end up removing all other listings from SignalR connection group
                props.setQuickBidError(false);
                props.resetAuctionData?.(props.AuctionBiddingData?.ListingID, data.AddedToWatchlist, data.UserBidInfo, skipListingDataLookup, (data.BidIncrement && data.BidIncrement != props.AuctionBiddingData.Increment) ? data.BidIncrement : null, props.AuctionBiddingData?.AuctionUpdateKey);
                addAuctionSiteDLVs({
                    listingId: props.AuctionBiddingData?.ListingID,
                    eventId: props.AuctionID,
                    eventName: props.AuctionName,
                    sellerId: props.SellerCRMID,
                    sellerName: props.dealerName,
                    userID: currentUser.CRMContactID,
                    gtmEvent: gtmEventType.quickBid,
                    bidAmount: nextBid,
                    bidValue: props.AuctionBiddingData?.Increment
                });
            }
            else if (data.IsPaused) {
                // Force a reset from the server if the bid result comes back as paused
                props.resetAuctionData?.(props.AuctionBiddingData?.ListingID, data.AddedToWatchlist, null);
                props.setWaitingForQuickBidAttemptResponseFlag(false);
            }
            else if (data.BidAgreementCheckFailed == true) {
                props.setQuickBidError(true);   
                setBidErrorDetails({
                    errorMessage: data.Message
                });
                setShowBiddingAgreement(true);  
                props.setWaitingForQuickBidAttemptResponseFlag(false);
            }
            else {
                props.setQuickBidError(true);
                props.setWaitingForQuickBidAttemptResponseFlag(false);
            }
        })
        .catch((error) => {
            props.setQuickBidError(true);
            props.setWaitingForQuickBidAttemptResponseFlag(false);
            setIsLoading(false);
        });
    }


    const okayToOpenBiddingModal = () => {

        //User has valid bid, need to show bid agreement, or user has accepted bid agreement but needs to go through pre-auth or vetting
        if ((props.showBoxOnly || (biddingAgreementData.BidAgreement.UserAccepted && biddingAgreementData.BidAgreement.UserIsApproved)) && validateBid() ||
            (!props.showBoxOnly && !biddingAgreementData && !showBiddingAgreement) ||
            (!props.showBoxOnly && biddingAgreementData && biddingAgreementData.BidAgreement &&
               (!biddingAgreementData.BidAgreement.UserAccepted ||
                biddingAgreementData.BidAgreement.PreAuthRequired ||
                (!biddingAgreementData.BidAgreement.VettingBidderStatus && biddingAgreementData.PaymentGateways?.length > 0)))) {
            return true;
        }

        return false;
    }

    const openBiddingModal = () => {
        if (!currentUser?.IsAuthenticated) {
            window.location.href = routes.SSO + '?to=' + encodeURIComponent(window.location.href);
        } else if (!isLoading && okayToOpenBiddingModal()) {

            if (currentSite?.Site?.IsAuctionTime && (!bidAgreementRequiredToBid || (bidAgreementRequiredToBid && biddingAgreementData && biddingAgreementData?.BidAgreement.UserIsApproved))) {
                extendAuctionTimeBidding(routes, props.AuctionBiddingData);

                const isLargeBid = props.IsAT && bidValue && (bidValue >= (props.AuctionBiddingData?.CurrentBid * 10));
                sandhillsLogging(routes.LogBidInformation, {
                    listingId: props.AuctionBiddingData?.ListingID,
                    bidValue: bidValue,
                    logLocation: isLargeBid ? 'largeBidModal' : 'biddingModal'
                });
            }
                        
            if (bidAgreementRequiredToBid && (!biddingAgreementData || (isSessionStorageSupported() && doesBrowserSessionShowUserAlreadyAcceptedBidAgreement(currentUser?.UserAuthID, props.AuctionBiddingData?.SaleID)))) {

                if (isSessionStorageSupported() && doesBrowserSessionShowUserAlreadyAcceptedBidAgreement(currentUser?.UserAuthID, props.AuctionBiddingData?.SaleID)) {
                    let bidAgreementText = biddingAgreementData?.BidAgreement?.AgreementText || (isSessionStorageSupported() && getBidAgreementTextForSaleFromSessionStorage(props.AuctionBiddingData?.SaleID)); 

                    //Session indicates user is approved to bid, so no need to get bid agreement.                    
                    setBiddingAgreementData({
                        BidAgreement: {
                            AgreementText: bidAgreementText,
                            BidAgreementAcceptanceRequired: true,
                            EventOrSaleID: props.AuctionBiddingData?.SaleID,
                            UserAccepted: true,
                            UserIsApproved: true
                        }
                    });
                    setIsLoading(false);
                    setShowBiddingAgreement(true);
                } else {
                    let routeAuctionEventID = props.AuctionBiddingData != null && props.AuctionBiddingData?.SaleID ? props.AuctionBiddingData?.SaleID : props.AuctionBiddingData?.EventID;
                    let getBiddingAgreementURL = routes.GetBiddingAgreementWithRefType.replace("{0}", getBidAgreementRefTypeFromECID(props.AuctionBiddingData?.EventCategoryID)).replace("{1}", routeAuctionEventID);

                    setIsLoading(true);

                    sandhillsRequest({
                        url: getBiddingAgreementURL,
                        errorHandling: {
                            message: 'An error occurred while retrieving the Terms and Conditions. Please refresh your page and try again.',
                            useConsole: false,
                            useAlert: true
                        }
                    }).then((data) => {
                        if (data?.BidAgreement) {
                            setBiddingAgreementData(data);
                            setIsLoading(false);
                            addBidAgreementTextToSessionStorage(props.AuctionBiddingData?.SaleID, data.BidAgreement.AgreementText);
                            if (data.BidAgreement.VettingBidderStatus && data.BidAgreement.VettingBidderStatus == bidderStatus.PENDING && !data.BidAgreement.UserIsApproved && !data.BidAgreement.PreAuthRequired) {
                                setBiddingStatusMessage(translator.translate('sandhills.tradepubwebsite.mvc.dealerreviewingrightsphone', [props.dealerName, data.EventPhoneNumber]));
                                setBidErrorDetails({
                                    errorMessage: translator.translate('sandhills.tradepubwebsite.mvc.dealerreviewingrightsphone', [props.dealerName, data.EventPhoneNumber])
                                });
                                addUpdateBAAcceptanceToSessionStorage(currentUser?.UserAuthID, props.AuctionBiddingData?.SaleID, false);
                                setShowBiddingAgreement(true);                            
                            }
                            else if (data?.BidAgreement?.VettingBidderStatus == bidderStatus.REVOKED) {
                                setBiddingStatusMessage(translator.translate('sandhills.bidcallerboard.mvc.dealerdeniedrights', [props.dealerName]));
                                setBidErrorDetails({
                                    errorMessage: translator.translate('sandhills.bidcallerboard.mvc.dealerdeniedrights', [props.dealerName])
                                });
                                addUpdateBAAcceptanceToSessionStorage(currentUser?.UserAuthID, props.AuctionBiddingData?.SaleID, false);
                                setShowBiddingAgreement(true);                            
                            }
                            else {
                                setShowBiddingAgreement(true);                            
                            }
                        }
                        else {
                            alert('An error occurred while retrieving the Terms and Conditions. Please refresh your page and try again.');
                        }
                    })
                        .catch((error) => {
                            setIsLoading(false);
                            setShowBiddingAgreement(false);
                        });
                }

            }
            else if (!bidAgreementRequiredToBid && props.IsAT) {
                //Go ahead and show max bid submission confirmation
                setBiddingAgreementData({
                    BidAgreement: {
                        AgreementText: '',
                        BidAgreementAcceptanceRequired: false,
                        EventOrSaleID: props.AuctionBiddingData?.SaleID,
                        UserAccepted: true,
                        UserIsApproved: true
                    }
                });
                setIsLoading(false);
                setShowBiddingAgreement(true);
            }
            else {
                setShowBiddingAgreement(!showBiddingAgreement);
                setIsLoading(false);
            }

        } else if (props.IsAT && bidAgreementRequiredToBid && biddingAgreementData && !biddingAgreementData?.BidAgreement.UserIsApproved) {
            setBiddingAgreementData(biddingAgreementData);
            setIsLoading(false);
            setShowBiddingAgreement(true);
        }
    };

    const toggleQuickBidSetting = (promptConfirmation) => {
        if (promptConfirmation && !quickBiddingEnabled) {
            setShowQuickBidActivationModal(true); // show modal to confirm enabling QuickBid
        }
        else {
            setShowQuickBidMoreInfo(false);
            setIsLoading(true);
            sandhillsRequest({
                url: routes.SaveUserQuickBidSettings,
                data: { quickBidEnabled: !quickBiddingEnabled },
                errorHandling: {
                    message: 'An error occurred while saving your Quick Bid setting. Please refresh your page and try again.',
                    useConsole: false,
                    useAlert: true
                }
            }).then((data) => {
                setQuickBiddingEnabled(!quickBiddingEnabled);
            })
            .finally(() => {
                setIsLoading(false);
            });
        }
    }

    const renderExpandedComponent = () => {
        if (biddingAgreementData?.BidAgreement?.UserAccepted) {
            if (!biddingAgreementData.BidAgreement.PreAuthRequired && !(!biddingAgreementData.BidAgreement.VettingBidderStatus && biddingAgreementData.PaymentGateways?.length > 0)) {
                //pending or revoked for given event
                if (biddingAgreementData.BidAgreement.VettingBidderStatus && biddingAgreementData.BidAgreement.VettingBidderStatus == bidderStatus.PENDING) {
                    return (<p>{translator.translate('sandhills.tradepubwebsite.mvc.dealerreviewingrightsphone', [props.dealerName, props.EventPhoneNumber])}</p>);
                }
                else if (biddingAgreementData.BidAgreement.VettingBidderStatus && biddingAgreementData.BidAgreement.VettingBidderStatus == bidderStatus.REVOKED) {
                    return (<p>{translator.translate('sandhills.bidcallerboard.mvc.dealerdeniedrights', [props.dealerName])}</p>);
                }
            }
        }
        return (renderBidBox());
    };

    const cantPlaceBidMessage = () => {
        if (!currentUser?.IsAuthenticated) {
            return (
                <>
                    <p dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.tradesites.mvc.mustbeloggedintobid') }}></p>

                    <div className="flex-row">
                        <div className="flex-col">
                            <a href={routes.SSO}
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.location.href = routes.SSO + '?to=' + encodeURIComponent(window.location.href);
                                }}
                                className="block-btn yellow-btn">
                                <span>{translator.translate('sandhills.ialreadyhaveaccountlogin')}</span>
                            </a>
                            <div style={{ marginTop: '8px', fontSize: '11px', textAlign: 'center', lineHeight: '1.35' }}>{translator.translate('sandhills.logintobidblurb')}</div>
                        </div>
                        <div className="flex-col">
                            <a href={routes.Register}
                                className="block-btn yellow-btn">
                                <span>{translator.translate('sandhills.ineedtoregistertobid')}</span>
                            </a>
                            <div style={{ marginTop: '8px', fontSize: '11px', textAlign: 'center', lineHeight: '1.35' }} >{translator.translate('sandhills.registertobidblurb')}</div>
                        </div>
                    </div>                    
                </>
            );
        }        
        if (!biddingAgreementData?.BidAgreement?.UserAccepted || (props.IsAT && !biddingAgreementData?.BidAgreement?.UserIsApproved) || isEFBidderWithPendingVettingBidderStatus) {            
            const translationParts = translator.translate('sandhills.youmustaccepttermsmessagewithlink').split('{0}');

            return (
                <div className="tc-box">
                    <p className="terms-and-conditions-must-accept">
                        {translationParts[0]}
                        <a
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowBiddingAgreement(true)}
                            className="show-terms-and-conditions-link"
                            dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.auctioneerstermsandconditions') }}
                        >
                        </a>
                        <span dangerouslySetInnerHTML={{ __html: translationParts[1] }} ></span>
                    </p>
                    <Button className='accept-terms-and-conditions-button' variant="contained" onClick={() => setShowBiddingAgreement(true)} sx={buttonStyles}>
                        {translator.translate('sandhills.signature.accept')}
                    </Button>
                </div>
            );
        }

        if (biddingAgreementData?.BidAgreement?.PreAuthRequired || (!biddingAgreementData?.BidAgreement?.VettingBidderStatus && biddingAgreementData?.PaymentGateways?.length > 0)) {
            return (<p dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.tradesites.mvc.mustcompleteccauth') }}></p>);
        }

        return null;
    };

    const renderBidBox = () => {
        return (
            <div className='bidding-box-container no-print'>
                <div className="bidding-box">
                    {props.IsAT && currentUser?.IsAuthenticated && currentUser?.BidderStatus == bidderStatus.ACTIVE && props.IsDetailPage && (!bidAgreementRequiredToBid || (bidAgreementRequiredToBid && biddingAgreementData.BidAgreement.UserAccepted && biddingAgreementData.BidAgreement.UserIsApproved)) &&                                                 
                        <>
                            <div className={quickBiddingEnabled ? "quick-bidding-box active" : "quick-bidding-box inactive"}>
                                <div className="quick-bidding-box_content">
                                    {quickBiddingEnabled ?
                                        <span>
                                            <span className="quick-bidding-box_title">{translator.translate('sandhills.quickbid')}</span><br />
                                            {translator.translate('sandhills.tradepubwebsite.mvc.yourbidsimmediate2')}
                                        </span>
                                        :
                                        <span>
                                            <span className="quick-bidding-box_title">{translator.translate('sandhills.quickbid')}</span><br />
                                            {translator.translate('sandhills.tradepubwebsite.mvc.placebidswithoneclick')} <span className='underline' role='button' tabIndex='0' onClick={() => { setShowQuickBidMoreInfo(!showQuickBidMoreInfo); }}>
                                                <span className="more-info">{showQuickBidMoreInfo ? translator.translate('sandhills.showless') : translator.translate('sandhills.tradepubwebsite.mvc.moreinfo')}</span>
                                            </span>
                                        </span>
                                    }
                                    <div className='quick-bidding-box__toggle' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleQuickBidSetting(true); } }} onClick={() => { toggleQuickBidSetting(true); }}>
                                        <span dangerouslySetInnerHTML={{ __html: quickBiddingEnabled ? translator.translate('sandhills.tradepubwebsite.mvc.turnoffquickbid') : translator.translate('sandhills.tradepubwebsite.mvc.turnonquickbid') }}></span> <i aria-hidden='true' class='fas fa-up-right-from-square'></i>
                                    </div>
                                </div>
                                {quickBiddingEnabled &&
                                    <>
                                        <p>
                                            <strong dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.biddingwebagreementdisclaimer', [routes.TermsOfUse]) }}></strong>
                                        </p>
                                        <>
                                        {props.AuctionBiddingData.UserBidInfo.UsersLastBid > 0 && (!props.AuctionBiddingData.UserBidInfo.UserIsOutBid && !props.AuctionBiddingData.UserBidInfo.UserIsTieBidder) ?
                                            <div className="quick-bidding-box_button">
                                                {translator.translate('sandhills.tradepubwebsite.mvc.highbidder').toUpperCase()}
                                            </div>
                                            :
                                            <Button className={(props.IsRedesign ? 'redesign-bid' : 'place-bid') + ' ' + (currentSite?.Site?.IsLivestockMarket || currentSite?.Site?.IsEquineMarket ? 'siteColorLSM' : '')} variant="contained" onClick={submitQuickBid} sx={buttonStyles}>
                                                {translator.translate('sandhills.tradepubwebsite.mvc.quickbid')}&nbsp;<strong>{props.AuctionBiddingData.NextBidAmount}</strong>
                                                </Button>
                                            }
                                        </>
                                    </>
                                }
                                {!quickBiddingEnabled && showQuickBidMoreInfo && <>
                                    <br/>
                                    <span dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.tradepubwebsite.mvc.enablequickbid') }}></span>&nbsp;
                                    {translator.translate('sandhills.tradepubwebsite.mvc.yourbidsimmediate2')}!&nbsp;
                                    {translator.translate('sandhills.tradepubwebsite.mvc.youwillreceivenoprompt').replace(/\{0\}/g, '"')}
                                </>}
                            </div>                                                    
                    </>}
                    <div className='bidding-box__info'>
                        {!props.hideCurrentBid &&
                            <div className='bidding-box__current-bid'>
                                {props.AuctionBiddingData?.BidLabel}: {props.AuctionBiddingData?.CurrentBidFormatted} {props.AuctionBiddingData?.QuantityLabel} 
                            </div>
                        }
                        {props.IsBuyersPremium &&
                            <div className='bidding-box__buyers-premium' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); props.getBuyersPremiumData(props.AuctionBiddingData?.ListingID, props.AuctionBiddingData?.CurrencyCode); } }} onClick={() => props.getBuyersPremiumData(props.AuctionBiddingData?.ListingID, props.AuctionBiddingData?.CurrencyCode)}>
                                <TranslationText tKey='Sandhills.ViewBuyersPremium' />
                            </div>
                        }
                        {!biddingStatusMessage && (props.AuctionBiddingData.BiddingAllowed || props.IsAT) &&
                            <strong className="bidding-box__bid-format">
                                {translator.translate('sandhills.placemaxbid')}&nbsp;{props.AuctionBiddingData?.CurrencySymbolText}:
                            </strong>
                        }
                    </div>
                    {biddingStatusMessage ?
                        <p className='bidding-box__status-message'>{biddingStatusMessage}</p>
                        :
                        <>
                            {(props.AuctionBiddingData.BiddingAllowed || props.IsAT) &&
                                <>
                                    <div className='bidding-box__place-bid'>
                                        {validationError &&
                                            <div>
                                                <strong className='bidding-box__error error-msg'>
                                                    {validationError}
                                                </strong>
                                            </div>
                                        }
                                        <TextField id="max-bid-val" variant="standard"
                                        inputProps={{ min: 0, inputMode: 'numeric', pattern: '[0-9]*', style: { ...textboxStyles, ...props.textboxInputStyles }, 'aria-label': (translator.translate('sandhills.placemaxbid') + " " + props.AuctionBiddingData?.CurrencySymbolText) }}
                                            sx={{ ...props.textboxStyles, width: '70%' }}
                                            value={bidValue}
                                            onChange={onBidValueChange}
                                            onBlur={() => {
                                                // Once they are one, convert to the proper value
                                                // parsed values of space or empty string returns NaN. Need parse first as isNaN spaces/empty strings returns false.
                                                if (props.IsEF) {
                                                    let parsedBidValue = parseInt(bidValue);
                                                    setBidValue(isNaN(parsedBidValue) ? "" : parsedBidValue);
                                                }
                                                else {
                                                    let parsedBidValue = parseFloat(bidValue).toFixed(2);
                                                    setBidValue(isNaN(parsedBidValue) ? "" : parsedBidValue);
                                                }
                                            }
                                            }
                                            className={props.IsRedesign ? 'redesign-textbox' : ''}
                                        />
                                    <Button className={(props.IsRedesign ? 'redesign-bid' : 'place-bid') + ' ' + (currentSite?.Site?.IsLivestockMarket || currentSite?.Site?.IsEquineMarket ? 'siteColorLSM' : '') + ' ' + (disablePlaceBidButton ? 'disabled' : '')} disabled={disablePlaceBidButton} variant="contained" onClick={openBiddingModal} sx={buttonStyles}>{translator.translate('sandhills.tradepubwebsite.mvc.placebid')}{(props.IsDetailPage && (!biddingAgreementData?.BidAgreement?.UserAccepted || (props.IsAT && !biddingAgreementData?.BidAgreement?.UserIsApproved))) ? ' *' : ''}</Button>
                                    </div>
                                    {props.IsDetailPage &&
                                        <div className='bid-data'>
                                            {props.IsAT &&
                                                <>
                                                    {props.AuctionBiddingData.AutoBidIncrementGroupID > 0 ?
                                                        <>
                                                            <Link className="auto-bid-increment-link" onClick={props.displayAutoBidIncrementModal}>{translator.translate('sandhills.dynamicbidincrements')} <Icon baseClassName='fas' className='fa-info-circle' /></Link>
                                                        </>
                                                        :
                                                        props.AuctionBiddingData.Increment > 0 ? <span>{translator.translate('Sandhills.TradePubWebSite.MVC.BidIncrement')} {props.AuctionBiddingData.Increment} </span> : ""}
                                                </>
                                            }
                                            {props.AuctionBiddingData?.NumberOfBids > 0 && <span> | {translator.translate('sandhills.tradepubwebsite.mvc.numberofbids')}: {props.AuctionBiddingData.NumberOfBids}</span>}
                                        </div>
                                    }
                                    {(props.AuctionBiddingData?.UserBidInfo && props.AuctionBiddingData.UserBidInfo?.UsersLastBid > 0 && !(props.IsAT && !props.IsDetailPage)) &&
                                        <>
                                            {props.IsAT ?
                                                <p className='bidding-box__user-last-bid'>{translator.translate('sandhills.tradepubwebsite.mvc.currentmaxbid')}:&nbsp;{props.AuctionBiddingData?.UserBidInfo.UsersLastBidFormatted}</p>
                                                :
                                                <p className='bidding-box__user-last-bid'>{translator.translate('sandhills.yourcurrentprebidis')}:&nbsp;{props.AuctionBiddingData?.UserBidInfo.UsersLastBidFormatted}</p>
                                            }
                                        </>
                                    }
                                </>
                            }
                        </>
                    }
                    {props.IsDetailPage &&                        
                        cantPlaceBidMessage()                                                                        
                    }
                </div>
            </div>
        );
    };


    const renderOnlyQuickBidButton = () => {
        return (
            <div className="quick-bidding-only-box active">
                {props.AuctionBiddingData.UserBidInfo?.UsersLastBid > 0 & (!props.AuctionBiddingData.UserBidInfo?.UserIsOutBid && !props.AuctionBiddingData.UserBidInfo?.UserIsTieBidder) ?
                    <div className="quick-bidding-box_button">
                        {translator.translate('sandhills.tradepubwebsite.mvc.highbidder').toUpperCase()}
                    </div>
                    :
                    <Button className={'list-quick-bid-btn ' + (currentSite?.Site?.IsLivestockMarket || currentSite?.Site?.IsEquineMarket ? 'siteColorLSM' : '')} variant="contained" onClick={submitQuickBid} sx={buttonStyles}>
                        {translator.translate('sandhills.tradepubwebsite.mvc.quickbid')}&nbsp;<strong>{props.AuctionBiddingData.NextBidAmount}</strong>
                    </Button>
                }
            </div>
        );  
    };

    const textboxStyles = {
        fontSize: '.875rem'
    };

    const buttonStyles = {
        fontSize: '.688rem',
        fontWeight: '700',
        padding: '8px 16px',
        borderRadius: '0',
        margin: '15px 10px 0 10px',
        width: '85%'
    };

    return (
        <>
            {showBiddingAgreement &&                 
                <BiddingAgreementModal
                biddingAgreement={biddingAgreementData}
                onBiddingAgreementDataUpdated={onBiddingAgreementDataUpdated}
                onClose={() => {
                    setShowBiddingAgreement(false);                    
                    if (props.IsAT) {
                        setBidValue(""); // Clear out the box on AT as this should only be what the user entered at the time of opening the modal
                    }
                }}
                auctionBiddingData={{ ...props.AuctionBiddingData, UserBid: bidValue }}
                resetAuctionData={props.resetAuctionData}
                notificationSettings={props.notificationSettings}
                WatchlistUrl={props.WatchlistUrl}
                IsDetailPage={props.IsDetailPage}
                listingCountry={props.listingCountry}
                EventStartDateTime={props.EventStartDateTime}
                BidPopUpURL={props.BidPopUpURL}
                IsAT={props.IsAT}
                IsEF={props.IsEF}
                SellerCRMID={props.SellerCRMID}
                dealerName={props.dealerName}
                AuctionID={props.AuctionID}
                AuctionName={props.AuctionName}
                AutoBidIncrementData={props.AutoBidIncrementData}
                BidErrorDetails={bidErrorDetails}                
                />}
            {showQuickBidActivationModal &&
                <EnableQuickBidModal
                    onClose={() => {
                        setShowQuickBidActivationModal(false);
                    }}
                    onConfirm={() => {
                        setShowQuickBidActivationModal(false);
                        toggleQuickBidSetting(false);
                    }}
                />}
            {(props.showBoxOnly && !(biddingAgreementData?.BidAgreement?.VettingBidderStatus && (biddingAgreementData?.BidAgreement?.VettingBidderStatus == bidderStatus.PENDING || biddingAgreementData?.BidAgreement?.VettingBidderStatus == bidderStatus.REVOKED || biddingAgreementData?.BidAgreement?.VettingBidderStatus == bidderStatus.DISCARD))) ?
                <>
                    {showOnlyQuickBidButton == true ?
                        renderOnlyQuickBidButton()
                        :
                        renderBidBox()
                    }
                </>
                :
                <>{showOnlyQuickBidButton == true ?
                    renderOnlyQuickBidButton()
                    :
                    renderExpandedComponent()}</>
            }
            <Backdrop
                open={isLoading}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress sx={{ willChange: 'transform', transform: 'translateZ(0)' }} />
            </Backdrop>
        </>
    );
};

export default ListingBiddingBox;